import { useState, useMemo } from 'react'
import type { DrinkEntry } from '@/types'
import { computeSessionIndices } from '@/utils/bac'
import { formatHistoryDate, formatNumber, mlToDisplay, volumeUnit } from '@/utils/time'
import DrinkDetailModal, { type EntryDetail } from './DrinkDetailModal'

interface DrinkHistoryProps {
  entries: DrinkEntry[]
  onEdit: (index: number) => void
  onCopy: (index: number) => void
  onDelete: (index: number) => void
}

const PAGE_SIZE = 25

export default function DrinkHistory({ entries, onEdit, onCopy, onDelete }: DrinkHistoryProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  // Tracks the open action menu by the entry's stable original index (not paginated position)
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)
  // Entry shown in the full-detail modal, by stable original index
  const [detailIndex, setDetailIndex] = useState<number | null>(null)

  // Pre-compute a lowercase searchable string per entry, plus its stable original index.
  // Built once per `entries` change so typing doesn't re-run formatHistoryDate on every keystroke.
  const indexedEntries = useMemo(
    () =>
      entries.map((entry, originalIndex) => {
        const parts = [
          formatHistoryDate(entry.finished_at),
          entry.finished_at,
          entry.is_gap ? 'gap' : entry.drink.drink,
        ]
        if (entry.started_at) {
          parts.push(formatHistoryDate(entry.started_at), entry.started_at)
        }
        return { entry, originalIndex, search: parts.join('\n').toLowerCase() }
      }),
    [entries]
  )

  // Session index per entry (null for gaps), using the same BAC-decay rule as
  // countSessions/allSessions so "session N" means the same thing everywhere.
  const sessionIndices = useMemo(() => computeSessionIndices(entries), [entries])

  // Per-session totals + each entry's position within its session, for the detail modal.
  const { sessionInfo, positions, totalSessions } = useMemo(() => {
    const info = new Map<number, { count: number; peakBac: number; start: string; end: string }>()
    const pos: (number | null)[] = new Array(entries.length).fill(null)
    let total = 0

    for (let i = 0; i < entries.length; i++) {
      const sessionIndex = sessionIndices[i]
      if (sessionIndex == null) continue

      const entry = entries[i]
      const existing = info.get(sessionIndex)

      if (!existing) {
        info.set(sessionIndex, {
          count: 1,
          peakBac: entry.bac || 0,
          start: entry.finished_at,
          end: entry.finished_at,
        })
        pos[i] = 1
        total = Math.max(total, sessionIndex + 1)
      } else {
        existing.count++
        existing.peakBac = Math.max(existing.peakBac, entry.bac || 0)
        existing.end = entry.finished_at
        pos[i] = existing.count
      }
    }

    return { sessionInfo: info, positions: pos, totalSessions: total }
  }, [entries, sessionIndices])

  // Filter on the precomputed index
  const filteredEntries = useMemo(() => {
    if (!searchTerm) return indexedEntries
    const term = searchTerm.toLowerCase()
    return indexedEntries.filter(item => item.search.includes(term))
  }, [indexedEntries, searchTerm])

  // Pre-compute durations (time since previous drink) based on original entries array
  const durations = useMemo(() => {
    const map = new Map<DrinkEntry, number | null>()
    for (let i = 0; i < entries.length; i++) {
      if (i === 0 || entries[i].is_gap || entries[i - 1].is_gap) {
        map.set(entries[i], null)
      } else {
        const curr = new Date(entries[i].finished_at).getTime()
        const prev = new Date(entries[i - 1].finished_at).getTime()
        const diffSeconds = (curr - prev) / 1000
        map.set(entries[i], diffSeconds > 0 ? diffSeconds : null)
      }
    }
    return map
  }, [entries])

  // Reverse to show newest first, then paginate. Items carry their stable originalIndex.
  const reversedEntries = useMemo(() => [...filteredEntries].reverse(), [filteredEntries])
  const totalPages = Math.ceil(reversedEntries.length / PAGE_SIZE)
  const paginatedEntries = reversedEntries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const handleDelete = (originalIndex: number) => {
    if (originalIndex < 0) return
    if (confirm('Are you sure you want to delete this entry?')) {
      onDelete(originalIndex)
    }
    setOpenMenuIndex(null)
  }

  // Assemble everything the detail modal needs for the selected row.
  const detail = useMemo((): EntryDetail | null => {
    if (detailIndex == null || detailIndex < 0 || detailIndex >= entries.length) return null

    const entry = entries[detailIndex]
    const sessionIndex = sessionIndices[detailIndex]
    const info = sessionIndex != null ? sessionInfo.get(sessionIndex) : undefined

    return {
      entry,
      originalIndex: detailIndex,
      sessionNumber: sessionIndex != null ? sessionIndex + 1 : null,
      totalSessions,
      positionInSession: positions[detailIndex],
      sessionDrinkCount: info?.count ?? null,
      sessionPeakBac: info?.peakBac ?? null,
      sessionStart: info?.start ?? null,
      sessionEnd: info?.end ?? null,
      secondsSincePrevious: durations.get(entry) ?? null,
    }
  }, [detailIndex, entries, sessionIndices, sessionInfo, positions, totalSessions, durations])

  return (
    <div className="card p-0 overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-[var(--border)]">
        <h3 className="font-semibold text-[var(--text-primary)]">
          History
        </h3>

        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-md">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(1)
            }}
            placeholder="Search by drink or date…"
            aria-label="Search history by drink name or date"
            className="w-full text-sm"
          />
        </div>

        {/* Record count */}
        <span className="text-sm text-[var(--text-secondary)]">
          {formatNumber(filteredEntries.length, 0)} records
        </span>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table-dark">
          <thead>
            <tr>
              <th>Date</th>
              <th>Drink</th>
              <th className="hidden md:table-cell">{volumeUnit()}</th>
              <th className="hidden md:table-cell">ABV</th>
              <th className="hidden md:table-cell">Cal</th>
              <th className="hidden md:table-cell">Duration</th>
              <th>BAC</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedEntries.map(({ entry, originalIndex }, displayIdx) => {
              const sessionIndex = sessionIndices[originalIndex]

              // Rows are newest-first, so a boundary is where the displayed row
              // above belongs to a different session (gaps count as their own block).
              const prevItem = displayIdx > 0 ? paginatedEntries[displayIdx - 1] : null
              const prevSessionIndex = prevItem ? sessionIndices[prevItem.originalIndex] : undefined
              const isSessionBoundary = displayIdx > 0 && prevSessionIndex !== sessionIndex

              // Alternating tint: session indices are contiguous, so neighbouring
              // sessions always land on opposite parities.
              const bandClass = sessionIndex != null && sessionIndex % 2 === 1 ? 'session-band' : ''
              const dividerClass = isSessionBoundary ? 'session-divider' : ''

              if (entry.is_gap) {
                return (
                  <tr key={originalIndex} className={`bg-[var(--bg-card)] ${dividerClass}`}>
                    <td colSpan={8} className="text-center py-4">
                      <button
                        onClick={() => setDetailIndex(originalIndex)}
                        className="text-[var(--text-secondary)] text-sm hover:text-[var(--accent-teal)] transition-colors cursor-pointer"
                      >
                        <span className="font-medium">Data Gap</span>
                        <br />
                        {formatHistoryDate(entry.started_at || '')} - {formatHistoryDate(entry.finished_at)}
                      </button>
                      <br />
                      <button
                        onClick={() => handleDelete(originalIndex)}
                        className="mt-2 text-xs text-[var(--bac-danger)] hover:underline"
                      >
                        Delete Gap
                      </button>
                    </td>
                  </tr>
                )
              }

              const sessionLabel =
                sessionIndex != null ? `session ${sessionIndex + 1} of ${totalSessions}` : ''

              return (
                <tr key={originalIndex} className={`${bandClass} ${dividerClass}`.trim()}>
                  <td className="whitespace-nowrap cell-flush">
                    <button
                      onClick={() => setDetailIndex(originalIndex)}
                      className="w-full h-full text-left px-4 py-3 text-sm hover:text-[var(--accent-teal)] transition-colors cursor-pointer"
                      aria-label={`Details for ${entry.drink.drink}, ${formatHistoryDate(entry.finished_at)}, ${sessionLabel}`}
                    >
                      {formatHistoryDate(entry.finished_at)}
                    </button>
                  </td>
                  <td className="max-w-[150px] lg:max-w-none cell-flush" title={entry.drink.drink}>
                    <button
                      onClick={() => setDetailIndex(originalIndex)}
                      className="w-full h-full text-left px-4 py-3 truncate lg:whitespace-normal lg:overflow-visible hover:text-[var(--accent-teal)] transition-colors cursor-pointer"
                      aria-label={`Details for ${entry.drink.drink}, ${formatHistoryDate(entry.finished_at)}, ${sessionLabel}`}
                    >
                      {entry.drink.drink}
                    </button>
                  </td>
                  <td className="hidden md:table-cell">{mlToDisplay(parseFloat(entry.drink.milliliters))}</td>
                  <td className="hidden md:table-cell">{entry.drink.alcohol}%</td>
                  <td className="hidden md:table-cell">{entry.drink.calories || '-'}</td>
                  <td className="hidden md:table-cell whitespace-nowrap text-sm">
                    {durations.get(entry) != null
                      ? `${Math.floor(durations.get(entry)! / 3600)}:${Math.floor((durations.get(entry)! % 3600) / 60).toString().padStart(2, '0')}`
                      : '-'}
                  </td>
                  <td className="font-mono">{(entry.bac || 0).toFixed(3)}</td>
                  <td>
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuIndex(openMenuIndex === originalIndex ? null : originalIndex)}
                        className="p-1 rounded hover:bg-[var(--bg-card)] transition-colors"
                        aria-label="Actions"
                      >
                        <svg className="w-5 h-5 text-[var(--text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </button>

                      {openMenuIndex === originalIndex && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenMenuIndex(null)}
                          />
                          <div className="absolute right-0 top-8 z-20 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-lg py-1 min-w-[120px]">
                            <button
                              onClick={() => {
                                setDetailIndex(originalIndex)
                                setOpenMenuIndex(null)
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--bg-secondary)] transition-colors"
                            >
                              Details
                            </button>
                            <button
                              onClick={() => {
                                if (originalIndex >= 0) onEdit(originalIndex)
                                setOpenMenuIndex(null)
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--bg-secondary)] transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                if (originalIndex >= 0) onCopy(originalIndex)
                                setOpenMenuIndex(null)
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--bg-secondary)] transition-colors"
                            >
                              Copy
                            </button>
                            <button
                              onClick={() => handleDelete(originalIndex)}
                              className="w-full px-4 py-2 text-left text-sm text-[var(--bac-danger)] hover:bg-[var(--bg-secondary)] transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}

            {paginatedEntries.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-8 text-[var(--text-secondary)]">
                  {searchTerm ? 'No drinks match your search' : 'No drinks logged yet'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 p-4 border-t border-[var(--border)]">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-[var(--bg-card)] text-[var(--text-secondary)] disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm text-[var(--text-secondary)]">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-[var(--bg-card)] text-[var(--text-secondary)] disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Full-detail modal for the selected row */}
      <DrinkDetailModal
        detail={detail}
        onClose={() => setDetailIndex(null)}
        onEdit={onEdit}
        onCopy={onCopy}
        onDelete={onDelete}
      />
    </div>
  )
}
